import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { MapPin, Plus, PencilSimple, Trash, Lightning, X, CreditCard } from '@phosphor-icons/react';
import api, { getErrorMessage } from '../utils/api';
import { toast } from 'sonner';

const Branches = () => {
  const [branches, setBranches] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchToToggle, setBranchToToggle] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: ''
  });

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      // Fetch all branches including inactive for Branch Management view
      const response = await api.get('/branches?include_inactive=true');
      setBranches(response.data);
    } catch (error) {
      toast.error('Failed to load branches');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedBranch) {
        await api.put(`/branches/${selectedBranch.branch_id}`, formData);
        toast.success('Branch updated!');
        setShowEditDialog(false);
      } else {
        await api.post('/branches', formData);
        toast.success('Branch created!');
        setShowAddDialog(false);
      }
      setFormData({ name: '', address: '', phone: '', email: '' });
      setSelectedBranch(null);
      fetchBranches();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save branch'));
    }
  };

  const handleEdit = (branch) => {
    setSelectedBranch(branch);
    setFormData({
      name: branch.name,
      address: branch.address || '',
      phone: branch.phone || '',
      email: branch.email || ''
    });
    setShowEditDialog(true);
  };

  const handleDelete = async (branchId, branchName) => {
    if (!window.confirm(`Deactivate branch "${branchName}"?`)) return;
    
    try {
      await api.delete(`/branches/${branchId}`);
      toast.success('Branch deactivated');
      fetchBranches();
    } catch (error) {
      toast.error('Failed to deactivate branch');
    }
  };

  const handleToggleSubscription = async (branchId, currentValue) => {
    const newValue = !currentValue;
    try {
      await api.put(`/branches/${branchId}/subscription-config`, null, {
        params: { subscription_required: newValue }
      });
      toast.success(`Subscription ${newValue ? 'enabled' : 'disabled'} for branch`);
      fetchBranches();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update subscription config'));
    }
  };

  const handleToggleActive = async (branch, newActiveState) => {
    // If deactivating, show warning dialog
    if (!newActiveState) {
      setBranchToToggle({ ...branch, newState: newActiveState });
      setShowDeactivateDialog(true);
      return;
    }
    
    // If activating, directly update
    await updateBranchActiveStatus(branch.branch_id, newActiveState);
  };

  const confirmDeactivation = async () => {
    if (branchToToggle) {
      await updateBranchActiveStatus(branchToToggle.branch_id, branchToToggle.newState);
      setShowDeactivateDialog(false);
      setBranchToToggle(null);
    }
  };

  const updateBranchActiveStatus = async (branchId, isActive) => {
    try {
      await api.put(`/branches/${branchId}`, { is_active: isActive });
      toast.success(isActive ? 'Branch activated successfully' : 'Branch deactivated successfully');
      fetchBranches();
    } catch (error) {
      toast.error('Failed to update branch status');
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-light text-[#2C4C3B] mb-2">Branch Management</h1>
            <p className="text-sm sm:text-base text-[#1C1C1A]/60">Manage your therapy center locations</p>
          </div>
          <Button onClick={() => setShowAddDialog(true)} className="w-full sm:w-auto bg-[#2C4C3B] min-h-[44px]">
            <Plus className="w-5 h-5 mr-2" />
            Add Branch
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {branches.map((branch) => (
            <Card 
              key={branch.branch_id} 
              className={`p-6 hover:shadow-lg transition-all ${
                !branch.is_active ? 'opacity-50 bg-gray-50' : ''
              }`}
              data-testid={`branch-card-${branch.branch_id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${!branch.is_active ? 'bg-gray-400' : 'bg-[#2C4C3B]'}`}>
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#2C4C3B]">{branch.name}</h3>
                    {!branch.is_active && (
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded mt-1 inline-block" data-testid="inactive-badge">
                        Inactive
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(branch)} data-testid={`edit-branch-${branch.branch_id}`}>
                    <PencilSimple className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleDelete(branch.branch_id, branch.name)} 
                    className="text-red-600"
                    data-testid={`delete-branch-${branch.branch_id}`}
                  >
                    <Trash className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {branch.address && <p className="text-sm text-[#1C1C1A]/60 mb-2">📍 {branch.address}</p>}
              {branch.phone && <p className="text-sm text-[#1C1C1A]/60 mb-2">📞 {branch.phone}</p>}
              {branch.email && <p className="text-sm text-[#1C1C1A]/60 mb-3">✉️ {branch.email}</p>}
              
              <div className="pt-3 border-t border-gray-200 space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor={`switch-${branch.branch_id}`} className="text-sm font-medium cursor-pointer">
                    {branch.is_active ? 'Active' : 'Inactive'}
                  </Label>
                  <Switch
                    id={`switch-${branch.branch_id}`}
                    checked={branch.is_active}
                    onCheckedChange={(checked) => handleToggleActive(branch, checked)}
                    data-testid={`toggle-branch-${branch.branch_id}`}
                    className="data-[state=checked]:bg-[#2C4C3B]"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor={`subscription-${branch.branch_id}`} className="text-sm font-medium cursor-pointer flex items-center gap-2">
                      <CreditCard size={16} />
                      Require Subscription
                    </Label>
                    <p className="text-xs text-gray-500 mt-0.5">Owner must pay to access app</p>
                  </div>
                  <Switch
                    id={`subscription-${branch.branch_id}`}
                    checked={branch.subscription_required || false}
                    onCheckedChange={() => handleToggleSubscription(branch.branch_id, branch.subscription_required)}
                    data-testid={`toggle-subscription-${branch.branch_id}`}
                    className="data-[state=checked]:bg-blue-600"
                  />
                </div>

                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.location.href = `/branches/${branch.branch_id}/billing`}
                  className="w-full text-xs"
                  data-testid={`configure-ai-${branch.branch_id}`}
                >
                  ⚡ Configure AI Billing
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {branches.length === 0 && (
          <Card className="p-12 text-center">
            <MapPin className="w-16 h-16 text-[#1C1C1A]/20 mx-auto mb-4" />
            <p className="text-[#1C1C1A]/60 mb-4">No branches yet</p>
            <Button onClick={() => setShowAddDialog(true)} className="bg-[#2C4C3B]">
              <Plus className="w-5 h-5 mr-2" />
              Add First Branch
            </Button>
          </Card>
        )}

        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Add New Branch</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Branch Name *</Label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Downtown Center"
                  data-testid="branch-name-input"
                />
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="123 Main St, City"
                  data-testid="branch-address-input"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="+1234567890"
                  data-testid="branch-phone-input"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="branch@center.com"
                  data-testid="branch-email-input"
                />
              </div>
              
              {/* AI Billing Method Selector (Admin only, during creation) */}
              <div className="pt-4 pb-2 border-t border-gray-200">
                <Label className="text-base font-semibold flex items-center gap-2 mb-3">
                  <Lightning size={20} className="text-yellow-500" weight="fill" />
                  AI Features Billing Method
                </Label>
                <p className="text-xs text-gray-500 mb-3">
                  Choose how this branch will access AI features. Once set, the branch owner will only see this option.
                </p>
                <div className="space-y-2">
                  <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="ai_billing_method"
                      value="none"
                      checked={formData.ai_billing_method === null}
                      onChange={() => setFormData({...formData, ai_billing_method: null})}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">No AI Access (Default)</div>
                      <div className="text-xs text-gray-500">Owner must configure billing later</div>
                    </div>
                  </label>
                  
                  <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="ai_billing_method"
                      value="own_key"
                      checked={formData.ai_billing_method === 'own_key'}
                      onChange={() => setFormData({...formData, ai_billing_method: 'own_key'})}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">Own API Key</div>
                      <div className="text-xs text-gray-500">Owner provides their own Emergent LLM key</div>
                    </div>
                  </label>
                  
                  <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="ai_billing_method"
                      value="credits"
                      checked={formData.ai_billing_method === 'credits'}
                      onChange={() => setFormData({...formData, ai_billing_method: 'credits'})}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">Subscription Credits (Razorpay)</div>
                      <div className="text-xs text-gray-500">Flexible recharge: AI ₹1.50/credit, Notifications ₹15/credit</div>
                    </div>
                  </label>
                </div>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => {
                  setShowAddDialog(false);
                  setFormData({ name: '', address: '', phone: '', email: '', ai_billing_method: null });
                }} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-[#2C4C3B]" data-testid="create-branch-button">
                  Create Branch
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Edit Branch</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Branch Name *</Label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Downtown Center"
                />
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="123 Main St, City"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="+1234567890"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="branch@center.com"
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => {
                  setShowEditDialog(false);
                  setFormData({ name: '', address: '', phone: '', email: '', ai_billing_method: null });
                  setSelectedBranch(null);
                }} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-[#2C4C3B]">
                  Update Branch
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Deactivate Branch</DialogTitle>
              <DialogDescription className="text-[#1C1C1A]/70 pt-2">
                Are you sure you want to deactivate &quot;{branchToToggle?.name}&quot;? 
                <br /><br />
                <strong>Note:</strong> This branch will be hidden from the admin dashboard. 
                However, existing check-ins and data will still be visible in branch-specific views.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowDeactivateDialog(false);
                  setBranchToToggle(null);
                }}
                data-testid="cancel-deactivate-button"
              >
                Cancel
              </Button>
              <Button 
                type="button" 
                className="bg-red-600 hover:bg-red-700"
                onClick={confirmDeactivation}
                data-testid="confirm-deactivate-button"
              >
                Yes, Deactivate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Branches;
